<template>
	<v-bottom-sheet v-model="sheet" content-class="rounded-t-lg">
		<v-form ref="form" @submit.prevent="submit">
			<v-card class="rounded-t-lg">
				<v-card-title class="justify-center text-h6">
					{{ $T('Visits.add-visit') }}
				</v-card-title>

				<v-card-text>
					<v-row dense>
						<v-col v-if="customersTypes.length" cols="12">
							<div class="text-body-2 mb-1">
								<required-label :text="$T('inputs.customer-type')" />
							</div>
							<v-select
								v-model="customerTypeId"
								:items="customersTypes"
								item-text="name"
								item-value="id"
								:rules="rules.required"
								outlined
								dense
								hide-details="auto"
								@change="onCustomerTypeChange"
							/>
						</v-col>

						<v-col cols="12">
							<div class="text-body-2 mb-1">
								<required-label :text="$T('inputs.region')" />
							</div>
							<v-autocomplete
								v-model="regionId"
								:items="regions"
								item-text="name"
								item-value="id"
								:rules="rules.required"
								outlined
								dense
								hide-details="auto"
								clearable
								@change="onRegionChange"
							/>
						</v-col>

						<v-col cols="12">
							<div class="text-body-2 mb-1">
								<required-label :text="$T('headers.customer')" />
							</div>
							<v-autocomplete
								v-model="entityId"
								:items="allEntities"
								item-text="name"
								item-value="id"
								:search-input.sync="entitySearchInput"
								:loading="entitiesLoading"
								:disabled="entitiesLoading"
								:rules="rules.required"
								outlined
								dense
								hide-details="auto"
								clearable
								append-icon="mdi-qrcode-scan"
								@click:append="openScanDialog"
							/>
						</v-col>
					</v-row>
				</v-card-text>

				<v-card-actions class="add-visit-bottom-sheet__actions pb-4">
					<v-btn color="primary" rounded block large type="submit">
						{{ $T('next') }}
					</v-btn>
					<v-btn outlined rounded block large class="mt-2 mx-0" @click="sheet = false">
						{{ $T('cancel') }}
					</v-btn>
				</v-card-actions>

				<v-progress-linear :value="33" height="3" color="primary" />
			</v-card>
		</v-form>

		<v-dialog v-model="scanDialog" max-width="560">
			<v-card>
				<v-toolbar flat color="primary" dark>
					<v-spacer />
					{{ $T('Visits.scan-qr') }}
					<v-spacer />
				</v-toolbar>

				<v-card-text class="pt-4">
					<qrcode-stream
						v-if="scanDialog"
						@decode="onQrDecode"
						@init="onQrInit"
					/>
					<p v-if="scanError" class="error--text mt-3 mb-0">{{ scanError }}</p>
				</v-card-text>

				<v-card-actions>
					<v-spacer />
					<v-btn text color="grey darken-1" @click="scanDialog = false">
						{{ $T('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-bottom-sheet>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { QrcodeStream } from 'vue-qrcode-reader'
import rules from '@/helpers/validation rules'
import { deletionExternal } from '@/configs/general.configs'
import RequiredLabel from '@/components/RequiredLabel.vue'

export default {
	name: 'AddVisitBottomSheet',

	components: {
		RequiredLabel,
		QrcodeStream
	},

	props: {
		// when true, submit only emits the selection instead of resetting the
		// draft and navigating — used to change the customer mid-flow
		skipReset: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			rules,
			moment,

			sheet: false,
			visitDate: null,
			customerTypeId: null,
			regionId: null,
			entityId: null,

			entitiesLoading: false,
			entitySearchInput: null,
			allEntities: [],

			scanDialog: false,
			scanError: null,
			scanHandled: false
		}
	},

	watch: {
		scanDialog(val) {
			if (!val) {
				this.scanError = null
				this.scanHandled = false
			}
		}
	},

	computed: {
		...mapState({
			entities: state => state.entities.entities,
			regions: state => state.regions.regions,
			customersTypes: state => deletionExternal
				? state.entities.customersTypes.filter(c => c.name !== 'Externals')
				: state.entities.customersTypes
		})
	},

	methods: {
		onCustomerTypeChange() {
			this.regionId = null
			this.entityId = null
			this.fetchEntities()
		},

		onRegionChange() {
			this.entityId = null
			this.fetchEntities()
		},

		fetchEntities() {
			this.entitiesLoading = true

			return this.$store.dispatch('entities/fetchAll', { entityType: this.customerTypeId, regionId: this.regionId })
				.then(() => {
					this.allEntities = Array.from(this.entities)
				})
				.finally(() => {
					this.entitiesLoading = false
				})
		},

		openScanDialog() {
			this.scanError = null
			this.scanHandled = false
			this.scanDialog = true
		},

		onQrInit(promise) {
			promise.catch(() => {
				this.scanError = this.$T('Visits.qr-camera-error')
			})
		},

		onQrDecode(rawValue) {
			if (this.scanHandled) return
			this.scanError = null
			this.scanHandled = true

			this.$store.dispatch('entities/fetchQr', { qrCodeData: rawValue })
				.then(entity => {
					// a readable code that matches no customer is not the same as an unreadable one
					if (!entity) return Promise.reject(new Error('qr-customer-not-found'))

					return this.applyScannedEntity(entity)
				})
				.then(() => {
					this.scanDialog = false
					this.$eventBus.$emit('show-snackbar', this.$T('Visits.qr-applied'))
				})
				.catch(error => {
					this.scanHandled = false
					this.scanError = error && error.message === 'qr-customer-not-found'
						? this.$T('Visits.qr-customer-not-found')
						: this.$T('Visits.qr-invalid')
				})
		},

		/*
			sets the type/region filters from the scanned customer, then refetches the list:
			v-autocomplete cannot show a value that is missing from its items, so the scanned
			customer is prepended when the filtered list does not contain it
		*/
		applyScannedEntity(entity) {
			if (this.customersTypes.length) {
				this.customerTypeId = this.customersTypes.some(type => type.id === entity.typeId) ? entity.typeId : null
			}

			if (this.regions.length) {
				this.regionId = this.regions.some(region => region.id === entity.regionId) ? entity.regionId : null
			}

			return this.fetchEntities()
				.catch(() => null)
				.then(() => {
					if (!this.allEntities.some(customer => customer.id === entity.id)) {
						this.allEntities = [entity, ...this.allEntities]
					}

					this.entityId = entity.id
				})
		},

		open(customerTypeId, date, { regionId = null, entityId = null } = {}) {
			if (this.$refs.form) this.$refs.form.resetValidation()

			this.customerTypeId = customerTypeId
			this.regionId = regionId
			this.entityId = entityId
			this.visitDate = moment().diff(date, 'days') <= 1 ? date : moment().format('YYYY-MM-DD')
			this.sheet = true

			if (!this.customersTypes.length) {
				this.$store.dispatch('entities/fetchEntityType')
			}

			if (!this.regions.length) {
				this.$store.dispatch('regions/fetchAll')
			}

			this.fetchEntities()
		},

		submit() {
			if (!this.$refs.form.validate()) return

			const selection = {
				customerTypeId: this.customerTypeId,
				entityId: this.entityId,
				visitDate: this.visitDate
			}

			if (this.skipReset) {
				this.sheet = false
				this.$emit('submit', selection)
				return
			}

			this.$store.dispatch('visitDraft/reset')
				.then(() => this.$store.dispatch('visitDraft/setCustomer', selection))
				.then(() => {
					this.sheet = false
					this.$router.push({
						name: 'add-visit-details',
						query: selection
					})
				})
		}
	}
}
</script>

<style scoped>
.add-visit-bottom-sheet__actions {
	display: flex;
	flex-direction: column;
	align-items: stretch;
}
</style>
